import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { Container, Row, Card, CardBody, CardTitle } from "reactstrap";
import FeedbackListContainer from "../../../Feedback/Components/Common/FeedbackList/FeedbackListContainer";
import { FeedbackCategory, FeedbackSortOption } from "../../../Feedback/Constants/feedbackEnums";
import {
  appPaths,
  LoadingStateHelper,
  ApiRequest,
  LoadingState,
  ResponseError,
  ResponseErrorHelper,
  ErrorDisplay
} from "../../../Common";
import { CompanyName } from "../Common/CompanyName";
import { endpoints } from "../../Constants/endpoints";
import ResponseHandler from "../../../App/ResponseHandler";
import { Company } from "../../Models/Company";
import { ResponseResult } from "../../../Common/Models/ResponseResult";
import { StatusCode } from "../../../App/Enums/StatusCode";

type TParams = {
  shortname: string;
};

type State = {
  company?: Company;
  loadingState: LoadingState;
  loadingError?: ResponseError;
};

const FEATURE_REQUEST = [FeedbackCategory.FeatureRequest];
const BUG_REPORT = [FeedbackCategory.BugReport];
const RESOURCE_REQUEST = [FeedbackCategory.ResourceRequest];

export default class CompanyProfileContainer extends React.Component<
  RouteComponentProps<TParams>,
  State
> {
  constructor(props: RouteComponentProps<TParams>) {
    super(props);

    this.state = {
      loadingState: {
        errored: false,
        isLoaded: false,
        isLoading: false
      }
    };
  }

  componentDidMount = () => {
    if (!this.state.loadingState.isLoaded && !this.state.loadingState.isLoading) {
      this.loadCompany();
    }
  };

  loadCompany = () => {
    this.setState({
      loadingState: LoadingStateHelper.generate(false, true, false)
    });

    ApiRequest.fromPath(endpoints.companies.getByShortname(this.props.match.params.shortname))
      .withAuthHeader()
      .get()
      .then(response => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult<Company>) => {
            this.setState(
              {
                company: result.payload
              },
              () => this.setState({ loadingState: LoadingStateHelper.generate(false, false, true) })
            );
          },
          result => {
            this.setState({
              loadingState: LoadingStateHelper.generate(true, false, false),
              loadingError: ResponseErrorHelper.generate(
                "Failed to load company",
                result.error.message,
                response.status
              )
            });
          },
          () => {
            this.setState({
              loadingState: LoadingStateHelper.generate(true, false, false),
              loadingError: ResponseErrorHelper.generate(
                "Failed to load company",
                "An unknown error has occured",
                response.status
              )
            });
          }
        ).catch(() => {
          this.setState({
            loadingState: LoadingStateHelper.generate(true, false, false),
            loadingError: ResponseErrorHelper.generate(
              "Failed to load company",
              "Unable to parse response JSON",
              response.status
            )
          });
        });
      })
      .catch(() => {
        this.setState({
          loadingState: LoadingStateHelper.generate(true, false, false),
          loadingError: ResponseErrorHelper.generate(
            "Failed to load company",
            "Failed to contact server",
            StatusCode.Status503ServiceUnavailable
          )
        });
      });
  };

  render() {
    if (this.state.loadingState.errored) {
      return <ErrorDisplay {...this.state.loadingError} />;
    }

    return (
      <Container fluid>
        <Row>
          <h2>
            <CompanyName
              shortname={this.props.match.params.shortname}
              company={this.state.company}
            />{" "}
            Public Profile
          </h2>
        </Row>
        <br />
        <Row>
          <Card style={{ width: "100%" }}>
            <CardBody>
              <CardTitle>
                <h4>Recent Feature Requests</h4>
              </CardTitle>
              <FeedbackListContainer
                listId={`APP_COMPANY_PROFILE_FEATURE_REQUEST_LIST${this.props.match.params.shortname}`}
                maxNumToGet={5}
                seeMoreLink={{
                  url: appPaths.company.featureRequests.replace(
                    ":shortname",
                    this.props.match.params.shortname
                  ),
                  text: "See All..."
                }}
                sort={FeedbackSortOption.DateCreated}
                isAscending={false}
                reorderable={false}
                shortname={this.props.match.params.shortname}
                virtualizeDisabled
                selectedCategories={FEATURE_REQUEST}
              />
            </CardBody>
          </Card>
        </Row>
        <br />
        <Row>
          <Card style={{ width: "100%" }}>
            <CardBody>
              <CardTitle>
                <h4>Recent Bug Reports</h4>
              </CardTitle>
              <FeedbackListContainer
                listId={`APP_COMPANY_PROFILE_BUG_REPORT_LIST${this.props.match.params.shortname}`}
                maxNumToGet={5}
                seeMoreLink={{
                  url: appPaths.company.bugReports.replace(
                    ":shortname",
                    this.props.match.params.shortname
                  ),
                  text: "See All..."
                }}
                sort={FeedbackSortOption.DateCreated}
                isAscending={false}
                reorderable={false}
                shortname={this.props.match.params.shortname}
                virtualizeDisabled
                selectedCategories={BUG_REPORT}
              />
            </CardBody>
          </Card>
        </Row>
        <br />
        <Row>
          <Card style={{ width: "100%" }}>
            <CardBody>
              <CardTitle>
                <h4>Recent Resource Requests</h4>
              </CardTitle>
              <FeedbackListContainer
                listId={`APP_COMPANY_PROFILE_RESOURCE_REQUEST_LIST${this.props.match.params.shortname}`}
                maxNumToGet={5}
                seeMoreLink={{
                  url: appPaths.company.resourceRequests.replace(
                    ":shortname",
                    this.props.match.params.shortname
                  ),
                  text: "See All..."
                }}
                sort={FeedbackSortOption.DateCreated}
                isAscending={false}
                reorderable={false}
                shortname={this.props.match.params.shortname}
                virtualizeDisabled
                selectedCategories={RESOURCE_REQUEST}
              />
            </CardBody>
          </Card>
        </Row>
        <br />
      </Container>
    );
  }
}
